import { mapDesigns } from "@mono/mapDesigns"
import { XMarkIcon } from "@heroicons/react/24/solid"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setModalData } from "@/store/globalSlice"
import ModalButton from "@/Components/ModalButton"
import { socket } from "@/utils/utils"
import PropertyBidCard from "@/Components/PropertyBigCard"
import Money from "@/Components/Money"
import ActionCard from "@/Components/ActionCard"
import { allChances } from "@mono/allChances"
import GameTimer from "@/Components/GameTimer"
import Modal from "@/Components/Modal"
import { useTimerTickSound } from "@/Hooks/useTimerTickSound"
import { useGameTimings } from "@/Hooks/useGameTimings"

const ForcedBuyModal: React.FC = () => {
  const dispatch = useAppDispatch()
  const myGame = useAppSelector((state) => state.global.myGame)
  // Only for `headerSize`, which is a prop rather than a class and so cannot be
  // answered with a `mobile:` step like everything else below.
  const isMobile = useAppSelector((state) => state.global.isMobile)
  const { cardDecisionTime } = useGameTimings()
  const modalData = useAppSelector((state) => state.global.modalData)
  const forcedBuyModal = modalData.forcedBuy
  const map = mapDesigns.find((map) => map.id === myGame?.settings.mapId)
  const property = map?.cells.find(
    (c) => c.order === forcedBuyModal?.propertyOrder
  )
  // Above the early return, so the claim is dropped as soon as the card is
  // answered rather than only when the modal is unmounted.
  useTimerTickSound(
    !!forcedBuyModal?.visible && !!property,
    "cardDecisionTimeLeft"
  )

  if (!forcedBuyModal?.visible || !property) return null

  const handleClose = () => {
    dispatch(setModalData({ forcedBuy: { visible: false } }))
  }

  const handleBuy = () => {
    if (!myGame?.id) return
    socket.emit("game.forcedBuyResponse", {
      roomid: myGame.id,
      propertyOrder: property.order,
      playerOrder: forcedBuyModal.playerOrder as PlayerOrder,
      played: true,
    })
    handleClose()
  }

  const handleDismiss = () => {
    if (!myGame?.id) return
    socket.emit("game.forcedBuyResponse", {
      roomid: myGame.id,
      propertyOrder: property.order,
      playerOrder: forcedBuyModal.playerOrder as PlayerOrder,
      played: false,
    })
    handleClose()
  }

  const forcedBuyCard = allChances.find((card) => card.id === "forced-buy")

  return (
    <Modal
      data-testid="game.forced-buy-modal-close"
      // Desktop keeps the viewport-centered 90% / max-w-2xl panel. On a phone
      // the panel is centered on the board. Horizontally that is the middle of
      // the play box: Pages/Game/index.tsx is `1fr auto 1fr` inside the
      // safe-area padding and both player columns are `(--play-w - 100vh)/2`
      // wide, so the 100vh square sits dead center of it — centering on the
      // viewport instead would land a notch's width off that square.
      // Vertically the square runs the full height of the screen (Board.tsx),
      // so its middle is plain 50vh.
      //
      // The panel hugs its content rather than taking a share of the width —
      // `w-[90%]` on a phone is ~760px of box around ~330px of content — and is
      // capped at the board, so it can never spill over the player columns.
      className={
        "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] w-[90%] max-w-2xl " +
        "mobile:top-[50vh] mobile:left-[calc(var(--safe-left)_+_var(--play-w)/2)] " +
        "mobile:w-auto mobile:max-w-[calc(100vh_-_12px)]"
      }
      // Below 1000px (`mobile:`) index.css halves the root font size, so every
      // rem-based step lands at half of what it reads as on a desktop. The
      // repo's answer is to restate the phone sizes in raw px (DoubleRentModal,
      // PropertyBigCardModal), and the header is part of that: its title and
      // subtitle would otherwise land at 12px and 7px.
      title={<span className="mobile:text-[15px]">Forced Buy</span>}
      subtitle={
        <span className="mobile:text-[10px]">
          Use the card to buy now, or pass it to auction
        </span>
      }
      // `sm` pulls the header's own paddings in with the title — the cheapest
      // height on a phone. The px sizes above hold either way.
      headerSize={isMobile ? "sm" : "default"}
      headerClassName="pt-6 pb-3 mobile:px-[10px] mobile:pt-[10px] mobile:pb-[6px]"
      // The stacked column is what overflowed: header, timer, name, price,
      // property card, action card and two buttons — `md:` is 930px, so both of
      // the rows below stack on a phone — ran to about twice a landscape
      // phone's height. On a phone it becomes two columns instead, the card on
      // the left and timer / name / buttons beside it, through a grid: that
      // places them without a second copy of the tree, so the DOM order still
      // reads top-to-bottom for the desktop flex column.
      //
      // The right column is a track sized off the board (0.45), not a fixed px
      // width, so the whole panel scales with it: 152px of card + 8 + track +
      // 16 of padding is 331px against a 344px board and 344px against a 374px
      // one. Sizing the *content* in px instead is what left the last version
      // looking off-center — a fixed-width block sitting left inside a panel
      // that the header had made wider.
      contentClassName={
        "flex flex-col items-center space-y-4 px-8 pb-6 text-white " +
        "mobile:grid mobile:grid-cols-[auto_calc(100vh*0.45)] mobile:items-center mobile:justify-center " +
        "mobile:gap-x-[8px] mobile:gap-y-[6px] mobile:space-y-0 mobile:px-[8px] mobile:pb-[10px]"
      }
    >
      {/* Timer */}
      <div className="w-full flex justify-center mobile:col-start-2 mobile:row-start-1">
        <GameTimer
          timerKey="cardDecisionTimeLeft"
          totalTime={cardDecisionTime}
          size="md"
          showLabel={true}
          labelText="Decision Time"
          // The ring is rem-sized like the rest of StaticTimer, so on a phone it
          // comes out at 28px — proportionate — but the seconds inside it drop
          // to 10px, and that number is the one thing here read at a glance.
          timeClassName="mobile:text-[13px]"
        />
      </div>

      {/* Property Info */}
      <div className="text-center space-y-3 mobile:col-start-2 mobile:row-start-2 mobile:w-full">
        <h2
          className="font-poppins text-3xl md:text-4xl font-semibold mobile:text-[16px] mobile:leading-tight"
          data-testid="game.buy-or-auction.name"
        >
          {property?.propertyDetails?.name}
        </h2>
        <div className="font-poppins text-xl md:text-3xl flex items-center justify-center gap-2 mobile:text-[11px] mobile:gap-[5px]">
          <span>Price:</span>
          <Money
            money={property?.propertyDetails?.price || 0}
            className="gap-2 text-3xl mobile:text-[15px] mobile:gap-[3px]"
            iconClass="h-2/3"
          />
        </div>
      </div>

      {/* Property Card and Action Card */}
      {/* The property card needs no phone size of its own: `normal` is the same
          table as the `auction-modal` AuctionResult uses, and it is written in
          rem — so the halved root font size takes it to 152x200, which is the
          size this column wants anyway. The action card is the one that sits
          the phone out: it is a fixed 144px of chance-card art next to a
          decision whose header and buy button already say the card is being
          spent, and the board has no width to spare for it. */}
      <div className="flex flex-col md:flex-row items-center justify-center gap-6 w-full mobile:col-start-1 mobile:row-start-1 mobile:row-span-3 mobile:w-auto">
        <PropertyBidCard cell={property} />
        {forcedBuyCard && (
          <ActionCard card={forcedBuyCard} className="mobile:hidden" />
        )}
      </div>

      {/* Buttons */}
      {/* Only the side padding is trimmed on a phone: `px-10` is 20px of gutter
          on either side of a 155px button. The vertical padding is left alone,
          so the buttons keep their full height. */}
      <div className="flex flex-col md:flex-row gap-4 w-full max-w-2xl mobile:col-start-2 mobile:row-start-3 mobile:flex-col mobile:gap-[8px] mobile:max-w-none">
        <ModalButton
          color="green"
          onClick={handleBuy}
          data-testid="game.forced-buy-modal-buy-btn"
          className="w-full mobile:px-[10px]"
        >
          <div className="flex items-center justify-center gap-2 whitespace-nowrap mobile:gap-[6px]">
            <i className="fa-cart-shopping fa-solid text-white fa-lg shrink-0"></i>
            <span className="text-white font-medium font-poppins text-lg mobile:text-[13px]">
              Use Card and Buy
            </span>
          </div>
        </ModalButton>
        <ModalButton
          color="red"
          onClick={handleDismiss}
          data-testid="game.forced-buy-modal-dismiss-btn"
          className="w-full mobile:px-[10px]"
        >
          <div className="flex items-center justify-center gap-2 whitespace-nowrap mobile:gap-[6px]">
            <XMarkIcon className="h-6 w-6 shrink-0 mobile:h-[14px] mobile:w-[14px]" />
            <span className="text-white font-medium font-poppins text-lg mobile:text-[13px]">
              Pass to Auction
            </span>
          </div>
        </ModalButton>
      </div>
    </Modal>
  )
}

export default ForcedBuyModal
